'use client';

import { Component, ReactNode } from 'react';
import * as Sentry from '@sentry/nextjs';

/**
 * Isolates a single widget's render failure so it degrades to nothing instead
 * of taking the page down with it.
 *
 * Deliberately a local class rather than `Sentry.ErrorBoundary`: that component
 * is re-exported from `@sentry/react` and only exists in the **browser** entry
 * of `@sentry/nextjs`. Under the `edge` / `edge-light` / `worker` / `workerd`
 * export conditions — the ones the Cloudflare Workers build resolves — the
 * package re-exports `@sentry/vercel-edge`, which has no `ErrorBoundary`, so
 * `Sentry.ErrorBoundary` is `undefined` there and React rejects the element
 * with "Element type is invalid", 500-ing every page that renders a widget.
 * `index.types.d.ts` still declares the export, so `tsc --noEmit` does not
 * catch it — only running the edge build does.
 *
 * `captureException` is present in every entry point, so reporting still works
 * wherever the boundary actually fires (the browser: React does not run
 * `componentDidCatch` during SSR).
 */
export class WidgetErrorBoundary extends Component<
    { children: ReactNode; fallback?: ReactNode },
    { hasError: boolean }
> {
    state = { hasError: false };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: { componentStack?: string | null }) {
        Sentry.captureException(error, {
            contexts: { react: { componentStack: errorInfo?.componentStack } },
        });
    }

    render() {
        if (this.state.hasError) return this.props.fallback ?? null;

        return this.props.children;
    }
}

export default WidgetErrorBoundary;
